/**
 * /de/produktvision — product vision concept page (DE). Targets produktvision
 * (20). Links /de/produkt-roadmap + /de/produktmanagement.
 */
import { MarketingShell } from './MarketingShell';

export function DeProduktvision() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/produktvision"
      hreflangLocales={['de']}
      documentTitle="Produktvision: Definition, Beispiel & Vorlage · Draftlize"
      documentDescription="Was eine starke Produktvision ausmacht, ein Beispiel, und wie du sie mit Roadmap und Entscheidungen verknüpfst, damit sie nicht zur Folie verkommt."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Leitfaden · Produktvision</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Die Produktvision,<br />
            <em>die mehr ist als eine Folie.</em>
          </h1>
          <p className="lede">
            Die Produktvision beschreibt den langfristigen Zustand, den das Produkt erreichen will – das <em>Wohin</em> und <em>Wofür</em>, über einzelne Features hinaus. Sie richtet das Team aus und begründet, warum die Roadmap so aussieht. Das Problem: Die meisten Visionen leben als schöne Folie, losgelöst von den täglichen Entscheidungen – und werden bedeutungslos. Hier: was eine gute Vision ausmacht, ein Beispiel, und wie du sie lebendig hältst.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Vision vs. Strategie vs. Roadmap.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Vision</strong> — der langfristige Zielzustand (das Wohin).</li>
            <li><strong>Strategie</strong> — wie ihr dorthin kommt (die Wahl der Wege).</li>
            <li><strong><a href="/de/produkt-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Roadmap</a></strong> — was in welcher Reihenfolge (die konkreten Schritte).</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            <strong>Beispiel:</strong> „Jedes Produktteam trifft Entscheidungen, die nie stillschweigend veralten.“ Kurz, richtungsweisend, überprüfbar. Eine gute Vision ist kein Slogan – sie leitet die Entscheidungen, die zu ihr führen. Teil des <a href="/de/produktmanagement" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Produktmanagements</a>.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Vision lebendig halten, <em>nicht als Folie.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Eine Vision wirkt nur, wenn die täglichen Entscheidungen auf sie einzahlen. Losgelöst wird sie zur Deko. In Draftlize verknüpfst du die Vision mit Roadmap und Entscheidungen – und siehst, was noch auf sie einzahlt und was abgedriftet ist.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Vision mit Entscheidungen verknüpft</h3>
            <p>Jede große Entscheidung verweist auf die Vision, auf die sie einzahlt – statt einer Folie, die niemand mehr öffnet.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Drift wird sichtbar</h3>
            <p>Kippt eine Entscheidung, die die Vision stützte, flaggt das Abhängige. Du siehst, wo Realität und Vision auseinanderlaufen.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Ein roter Faden für Team und Agent</h3>
            <p>Vision, Roadmap und Entscheidungen an einem Ort – lesbar für das Team und für deinen KI-Agenten via MCP.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Eine Produktvision scheitert nicht am Formulieren. Sie scheitert, wenn keine tägliche Entscheidung mehr auf sie einzahlt.
        </blockquote>
        <cite>Verbinde Vision, Roadmap und Entscheidungen an einem Ort. Das ist Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Vision vs. Mission?', a: 'Die Mission ist der Daseinszweck (warum es das Unternehmen gibt), die Vision der angestrebte Zukunftszustand des Produkts. Beide geben Richtung, auf unterschiedlicher Flughöhe.' },
            { q: 'Wie formuliert man eine gute Vision?', a: 'Kurz, richtungsweisend, überprüfbar – ein Zielzustand, kein Feature. Entscheidend ist, dass Entscheidungen sich daran messen lassen.' },
            { q: 'Wie oft anpassen?', a: 'Selten – die Vision ist stabil. Was sich ändert, sind die Entscheidungen auf dem Weg dorthin; die sollten nachvollziehbar bleiben.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Verbinde Vision, Roadmap und Entscheidungen <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Halte die Produktvision mit den Entscheidungen verknüpft, die auf sie einzahlen. Driftet etwas ab, siehst du es – statt einer Folie, die niemand mehr öffnet.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
